import { useTranslation } from "react-i18next";
import { FiFileText } from "react-icons/fi";
import type { PortalSourcingEventDetailDto } from "../../../../autogen/bff-api";
import { momentLocaleFor } from "../../../../common/formatting/i18nFormatters";
import { countyName } from "../../../../common/procurement/norwegianCounties";
import { formatEstimatedValue, formatShortDate, getContractNatureLabel } from "../utils/displayFormatters";
import { frameworkCompetitionRows } from "./frameworkCompetitionRows";
import { LabelValueGrid } from "./LabelValueGrid";
import { SectionCard } from "./SectionCard";

export const EventContractDetails = ({ event }: { event: PortalSourcingEventDetailDto }) => {
  const { t, i18n } = useTranslation();
  const locale = momentLocaleFor(i18n.language);
  const county = event.placeOfPerformanceCounty
    ? (countyName(event.placeOfPerformanceCounty) ?? event.placeOfPerformanceCounty)
    : undefined;
  const placeOfPerformance = [county, event.placeOfPerformanceDetail].filter(Boolean).join(", ");

  const rows = [
    event.estimatedValue?.value != null && {
      label: t("Estimated value"),
      value: formatEstimatedValue(event.estimatedValue.value, event.estimatedValue.currency),
    },
    event.contractNature && {
      label: t("Contract type"),
      value: getContractNatureLabel(event.contractNature, t),
    },
    placeOfPerformance && { label: t("Place of performance"), value: placeOfPerformance },
    event.contractStartDate && { label: t("Planned start"), value: formatShortDate(event.contractStartDate, locale) },
    event.contractEndDate && {
      label: t("Duration"),
      value: t("Until {{date}}", { date: formatShortDate(event.contractEndDate, locale) }),
    },
  ]
    .filter((row): row is { label: string; value: string } => Boolean(row))
    .concat(frameworkCompetitionRows(event, t));

  if (rows.length === 0) return null;

  return (
    <SectionCard icon={FiFileText} title={t("Contract details")}>
      <LabelValueGrid rows={rows} />
    </SectionCard>
  );
};
